import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiHome, FiPackage, FiUsers, FiUserCheck, FiGrid, FiBookOpen, 
  FiShare2, FiSettings, FiLogOut, FiBriefcase, FiBarChart2, 
  FiUser, FiActivity, FiShield, FiTag, FiMail, FiMessageSquare,
  FiTrendingUp, FiDatabase, FiAward, FiStar, FiTruck, FiChevronDown,
  FiChevronUp, FiHeart, FiSend, FiX
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

// Menu sections configuration
const menuSections = [
  {
    title: 'MAIN',
    icon: null,
    items: [
      { path: '/', icon: FiHome, label: 'Dashboard', permission: null }
    ]
  },
  {
    title: 'MANAGEMENT',
    icon: null,
    items: [
      { path: '/orders', icon: FiPackage, label: 'Orders', permission: 'orders_read' },
      { path: '/drivers', icon: FiUsers, label: 'Drivers', permission: 'drivers_read' },
      { path: '/tour-guides', icon: FiUserCheck, label: 'Tour Guides', permission: 'tour_guides_read' },
      { path: '/tour-companies', icon: FiBriefcase, label: 'Tour Companies', permission: 'tour_companies_read' },
      { path: '/services', icon: FiGrid, label: 'Services', permission: 'services_read' }
    ]
  },
  {
    title: 'CONTENT',
    icon: null,
    items: [
      { path: '/blog', icon: FiBookOpen, label: 'Blog Posts', permission: 'blog_read' },
      { path: '/blog/comments', icon: FiMessageSquare, label: 'Comments', permission: 'blog_read' }
    ]
  },
  {
    title: 'MARKETING',
    icon: null,
    items: [
      { path: '/affiliates', icon: FiShare2, label: 'Affiliates', permission: 'affiliates_read' },
      { path: '/coupons', icon: FiTag, label: 'Coupons', permission: 'coupons_read' },
      { path: '/newsletter', icon: FiMail, label: 'Newsletter', permission: 'settings_read' },
      { path: '/promotional-emails', icon: FiSend, label: 'Promo Emails', permission: 'settings_update' }
    ]
  },
  {
    title: 'REVIEWS & RATINGS',
    icon: null,
    items: [
      { path: '/reviews', icon: FiStar, label: 'Reviews', permission: 'reviews_read' },
      { path: '/leaderboard', icon: FiAward, label: 'Leaderboard', permission: 'reports_view' }
    ]
  },
  {
    title: 'ANALYTICS',
    icon: null,
    items: [
      { path: '/reports', icon: FiBarChart2, label: 'Reports', permission: 'reports_view' },
      { path: '/analytics', icon: FiTrendingUp, label: 'Analytics', permission: 'reports_view' }
    ]
  },
  {
    title: 'SYSTEM',
    icon: null,
    items: [
      { path: '/users', icon: FiUsers, label: 'Users', permission: 'users_read' },
      { path: '/roles', icon: FiShield, label: 'Roles & Permissions', permission: 'roles_read' },
      { path: '/system-health', icon: FiActivity, label: 'System Health', permission: 'system_read' },
      { path: '/backups', icon: FiDatabase, label: 'Backups', permission: 'backup_read' },
      { path: '/activity-log', icon: FiActivity, label: 'Activity Log', permission: 'activity_read' },
      { path: '/settings', icon: FiSettings, label: 'Settings', permission: 'settings_read' }
    ]
  },
  {
    title: 'ACCOUNT',
    icon: null,
    items: [
      { path: '/profile', icon: FiUser, label: 'Profile', permission: null }
    ]
  }
];

// Section Component with collapsible functionality
const MenuSection = ({ title, items, hasPermission }) => {
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();
  
  const hasVisibleItems = items.some(item => !item.permission || hasPermission(item.permission));
  
  if (!hasVisibleItems) return null;
  
  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-2 text-xs font-semibold text-primary-300 uppercase tracking-wider hover:text-primary-200 transition"
      >
        <span>{title}</span>
        {isOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
      </button>
      
      {isOpen && (
        <div className="space-y-1">
          {items.map((item) => {
            if (item.permission && !hasPermission(item.permission)) return null;
            
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-6 py-2.5 mx-2 rounded-lg transition ${
                  isActive 
                    ? 'bg-primary-800 text-white' 
                    : 'text-primary-200 hover:bg-primary-700 hover:text-white'
                }`}
              >
                <item.icon className="w-4 h-4 mr-3" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Sidebar({ onClose }) {
  const { logout, hasPermission, user } = useAuth();

  return (
    <div className="w-64 bg-primary-700 text-white flex flex-col shadow-lg h-full">
      {/* Logo & Close Button */}
      <div className="p-5 border-b border-primary-600 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold tracking-wide">Redsea Road</h1>
          <p className="text-xs text-primary-300 mt-1">Admin Panel</p>
          {user && (
            <p className="text-xs text-primary-400 mt-2 capitalize truncate">
              {user.role?.name || user.role?.code || 'User'}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1 rounded-lg hover:bg-primary-600 transition"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {menuSections.map((section, idx) => (
          <MenuSection
            key={idx}
            title={section.title}
            items={section.items}
            hasPermission={hasPermission}
          />
        ))}
      </nav>
      
      {/* Logout Button */}
      <div className="p-4 border-t border-primary-600">
        <button
          onClick={logout}
          className="flex items-center w-full px-4 py-2 text-primary-300 hover:text-white hover:bg-primary-600 rounded-lg transition"
        >
          <FiLogOut className="w-4 h-4 mr-3" />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </div>
  );
}
